import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toPaise, lineAmount, sumPaise } from '@vns/core';
import { supabase } from './supabase';

/**
 * Bulk material entry — one supplier bill, many material lines.
 *
 * The whole bill is written by a single database function so it is one
 * transaction. Posting the header and then each line from the browser would
 * mean a dropped connection halfway through leaves a bill with three of its
 * five materials, and nothing would ever notice or repair it.
 *
 * Line amounts are recomputed in the database too. What we send is quantity and
 * rate; what gets booked is the product of them. The figures below exist so the
 * user can see the total before saving, not so the client can decide it.
 */

export interface BillLine {
  material_id: string;
  unit_id: string;
  /** As typed. Kept as a string so a half-typed "1." does not become NaN. */
  quantity: string;
  rateRupees: string;
  remarks: string;
}

export const emptyLine = (): BillLine => ({
  material_id: '', unit_id: '', quantity: '', rateRupees: '', remarks: '',
});

export interface BillDraft {
  date: string;
  supplier_id: string;
  bill_no: string;
  /** The printed total, if the user typed one. Blank means "use the lines". */
  billTotalRupees: string;
  remarks: string;
  lines: BillLine[];
}

/* ------------------------------ arithmetic ------------------------------ */

/** Null when the line is not yet complete enough to price. */
export function lineTotalPaise(l: BillLine): number | null {
  const qty = Number(l.quantity);
  if (!l.quantity.trim() || !Number.isFinite(qty) || qty <= 0) return null;
  if (!l.rateRupees.trim()) return null;
  try {
    return lineAmount(qty, toPaise(l.rateRupees));
  } catch {
    return null;                      // a malformed rate prices as nothing yet
  }
}

export function linesTotalPaise(lines: BillLine[]): number {
  return sumPaise(lines.map((l) => lineTotalPaise(l) ?? 0));
}

/**
 * The gap between what the supplier printed and what the lines come to.
 *
 * Not an error. Bills routinely carry freight, loading or a round-off that no
 * material line accounts for, so this is shown as a fact for the admin to judge
 * — which is exactly what purchase_bills.bill_total_paise was added for.
 */
export function billDifferencePaise(draft: BillDraft): number | null {
  if (!draft.billTotalRupees.trim()) return null;
  try {
    return toPaise(draft.billTotalRupees) - linesTotalPaise(draft.lines);
  } catch {
    return null;
  }
}

/* ------------------------------ validation ------------------------------ */

export interface BillErrors {
  supplier_id?: string;
  date?: string;
  billTotalRupees?: string;
  /** Keyed by line index. */
  lines: Record<number, Partial<Record<keyof BillLine, string>>>;
  /** Something wrong with the bill as a whole. */
  form?: string;
}

export function validateBill(draft: BillDraft): BillErrors {
  const e: BillErrors = { lines: {} };
  if (!draft.supplier_id) e.supplier_id = 'Choose a supplier';
  if (!draft.date) e.date = 'Enter the bill date';

  const filled = draft.lines.filter((l) => l.material_id || l.quantity.trim() || l.rateRupees.trim());
  if (!filled.length) e.form = 'Add at least one material';

  const seen = new Map<string, number>();
  draft.lines.forEach((l, i) => {
    const touched = l.material_id || l.quantity.trim() || l.rateRupees.trim();
    if (!touched) return;                       // a blank spare row is not an error
    const le: Partial<Record<keyof BillLine, string>> = {};

    if (!l.material_id) le.material_id = 'Choose a material';
    else {
      // The same material twice on one bill is nearly always a mistake, and
      // silently accepting it makes the stock figure hard to argue with later.
      const first = seen.get(l.material_id);
      if (first !== undefined) le.material_id = `Same as line ${first + 1}`;
      else seen.set(l.material_id, i);
    }

    const qty = Number(l.quantity);
    if (!l.quantity.trim() || !Number.isFinite(qty) || qty <= 0) le.quantity = 'Enter a quantity';
    if (!l.rateRupees.trim()) le.rateRupees = 'Enter a rate';
    else {
      try { toPaise(l.rateRupees); } catch { le.rateRupees = 'Not a valid amount'; }
    }

    if (Object.keys(le).length) e.lines[i] = le;
  });

  if (draft.billTotalRupees.trim()) {
    try { toPaise(draft.billTotalRupees); } catch { e.billTotalRupees = 'Not a valid amount'; }
  }
  return e;
}

export function billHasErrors(e: BillErrors): boolean {
  return !!(e.supplier_id || e.date || e.billTotalRupees || e.form || Object.keys(e.lines).length);
}

/* -------------------------------- writing -------------------------------- */

export function useCreateBill(projectId: string) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (draft: BillDraft): Promise<string> => {
      const errors = validateBill(draft);
      if (billHasErrors(errors)) throw new Error(errors.form ?? 'Check the highlighted fields');

      const lines = draft.lines
        .filter((l) => l.material_id && l.quantity.trim() && l.rateRupees.trim())
        .map((l) => ({
          material_id: l.material_id,
          unit_id: l.unit_id || null,
          quantity: Number(l.quantity),
          rate_paise: toPaise(l.rateRupees),
          remarks: l.remarks.trim() || null,
        }));

      const { data, error } = await supabase.rpc('create_purchase_bill', {
        p_project_id: projectId,
        p_supplier_id: draft.supplier_id,
        p_bill_date: draft.date,
        p_lines: lines,
        // undefined rather than null: these carry SQL defaults, and omitting an
        // argument is how PostgREST asks for the default.
        p_bill_no: draft.bill_no.trim() || undefined,
        p_bill_total_paise: draft.billTotalRupees.trim() ? toPaise(draft.billTotalRupees) : undefined,
        p_remarks: draft.remarks.trim() || undefined,
      });
      if (error) throw new Error(error.message);
      return data as string;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['project'] });
      void qc.invalidateQueries({ queryKey: ['projects'] });
      void qc.invalidateQueries({ queryKey: ['project-stock-map'] });
      void qc.invalidateQueries({ queryKey: ['bills'] });
      void qc.invalidateQueries({ queryKey: ['suppliers'] });
      void qc.invalidateQueries({ queryKey: ['supplier'] });
      void qc.invalidateQueries({ queryKey: ['approvals'] });
      void qc.invalidateQueries({ queryKey: ['my-submissions'] });
      void qc.invalidateQueries({ queryKey: ['materials'] });
      void qc.invalidateQueries({ queryKey: ['material'] });
      void qc.invalidateQueries({ queryKey: ['material-full-report'] });
      void qc.invalidateQueries({ queryKey: ['material-report-picker'] });
      void qc.invalidateQueries({ queryKey: ['report'] });
      void qc.invalidateQueries({ queryKey: ['dashboard'] });
      void qc.invalidateQueries({ queryKey: ['staff-dashboard'] });
    },
  });
}

/* -------------------------------- reading -------------------------------- */

export interface BillLineRow {
  id: string;
  materialName: string;
  unit: string | null;
  quantity: number;
  ratePaise: number;
  amountPaise: number;
  remarks: string | null;
}

export interface BillRow {
  id: string;
  billNo: string | null;
  billDate: string;
  supplierName: string;
  billTotalPaise: number;
  linesTotalPaise: number;
  status: 'pending' | 'approved' | 'rejected';
  remarks: string | null;
  lines: BillLineRow[];
}

/** Bills booked against one project, newest first, with their lines. */
export function useProjectBills(projectId: string) {
  return useQuery({
    queryKey: ['bills', projectId],
    enabled: !!projectId,
    queryFn: async (): Promise<BillRow[]> => {
      const { data, error } = await supabase
        .from('purchase_bills')
        .select(`id, bill_no, bill_date, bill_total_paise, approval_status, remarks,
                 suppliers(name),
                 material_purchases(id, quantity, rate_paise, amount_paise, remarks,
                                    materials(name), units(short_name))`)
        .eq('project_id', projectId)
        .is('deleted_at', null)
        .order('bill_date', { ascending: false })
        .limit(100);
      if (error) throw new Error(error.message);

      return (data ?? []).map((b) => {
        const raw = (b.material_purchases ?? []) as unknown as Array<{
          id: string; quantity: number; rate_paise: number; amount_paise: number;
          remarks: string | null;
          materials: { name: string } | null;
          units: { short_name: string } | null;
        }>;
        const lines: BillLineRow[] = raw.map((l) => ({
          id: l.id,
          materialName: l.materials?.name ?? '—',
          unit: l.units?.short_name ?? null,
          quantity: Number(l.quantity),
          ratePaise: l.rate_paise,
          amountPaise: l.amount_paise,
          remarks: l.remarks,
        }));
        return {
          id: b.id,
          billNo: b.bill_no,
          billDate: b.bill_date,
          supplierName: (b.suppliers as unknown as { name: string } | null)?.name ?? '—',
          billTotalPaise: b.bill_total_paise,
          linesTotalPaise: sumPaise(lines.map((l) => l.amountPaise)),
          status: b.approval_status as BillRow['status'],
          remarks: b.remarks,
          lines,
        };
      });
    },
  });
}
